import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const AdminManagement = ({ setMessage }) => {
    const { getAllAdmins, removeAdmin, currentUser } = useAdmin();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadAdmins();
    }, []);

    const loadAdmins = async () => {
        try {
            setLoading(true);
            const adminsData = await getAllAdmins();
            setAdmins(adminsData);
        } catch (error) {
            setMessage('Error loading admin list: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // AdminManagement.js - handleRemoveAdmin function update করুন

    const handleRemoveAdmin = async (userId, userName) => {
        if (!window.confirm(`Are you sure you want to remove admin privileges from ${userName}?`)) {
            return;
        }

        try {
            console.log(`🔄 Removing admin privileges from user: ${userName} (${userId})`);
            await removeAdmin(userId);
            setMessage(`Admin privileges removed from ${userName} successfully!`);
            await loadAdmins();
        } catch (error) {
            console.error('❌ Error removing admin:', error);

            // More specific error messages
            if (error.message.includes('Server error')) {
                setMessage('Server error occurred. Please check backend logs.');
            } else if (error.message.includes('cannot remove your own')) {
                setMessage('You cannot remove your own admin privileges.');
            } else {
                setMessage('Error removing admin: ' + error.message);
            }
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Check if admin is the current user
    const isCurrentUser = (admin) => {
        if (!currentUser || !admin) return false;

        // Compare by ID or email
        if (admin._id && currentUser.id && admin._id.toString() === currentUser.id.toString()) {
            return true;
        }

        if (admin.email && currentUser.email && admin.email === currentUser.email) {
            return true;
        }

        return false;
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading admin list...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="h3 mb-1 fw-bold">Admin Management</h2>
                    <p className="text-muted mb-0">Manage admin users and their privileges</p>
                </div>
                <div className="badge bg-primary fs-6">
                    <i className="fas fa-users me-1"></i>
                    Total Admins: {admins.length}
                </div>
            </div>

            {admins.length === 0 ? (
                <div className="text-center py-5">
                    <div className="mb-4">
                        <i className="fas fa-users fa-3x text-muted"></i>
                    </div>
                    <h4 className="text-muted">No Admins Found</h4>
                    <p className="text-muted">There are no admin users in the system.</p>
                </div>
            ) : (
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-light">
                        <h5 className="card-title mb-0 fw-semibold">
                            <i className="fas fa-user-shield me-2"></i>
                            Admin Users
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th scope="col" className="ps-4">Admin</th>
                                        <th scope="col">Email</th>
                                        <th scope="col">Admin Since</th>
                                        <th scope="col" className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {admins.map((admin) => {
                                        const isCurrent = isCurrentUser(admin);
                                        return (
                                            <tr
                                                key={admin._id || admin.id}
                                                className={isCurrent ? 'table-info' : ''}
                                            >
                                                <td className="ps-4">
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-shrink-0">
                                                            {admin.photoURL ? (
                                                                <img
                                                                    src={admin.photoURL}
                                                                    alt={admin.name}
                                                                    className="rounded-circle"
                                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                />
                                                            ) : (
                                                                <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                                                                    style={{ width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold' }}>
                                                                    {admin.name?.charAt(0).toUpperCase() || 'A'}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-grow-1 ms-3">
                                                            <div className="fw-medium">
                                                                {admin.name}
                                                                {isCurrent && (
                                                                    <span className="badge bg-success ms-2">
                                                                        <i className="fas fa-user me-1"></i>
                                                                        You
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="text-break">{admin.email}</span>
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {formatDate(admin.approvedAt || admin.createdAt)}
                                                    </small>
                                                </td>
                                                <td className="text-center">
                                                    {!isCurrent ? (
                                                        <button
                                                            onClick={() => handleRemoveAdmin(admin._id, admin.name)}
                                                            className="btn btn-outline-danger btn-sm"
                                                            title="Remove Admin Privileges"
                                                        >
                                                            <i className="fas fa-user-times me-1"></i>
                                                            Remove Admin
                                                        </button>
                                                    ) : (
                                                        <span className="text-muted small">Current User</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminManagement;